import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { BrokerAccountInputSelector, setSelectedBrokerAccountId } from './reducers/BrokerAccountInputReducer';
import BrokerAccountModel from './models/BrokerAccountModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import Swal from 'sweetalert2';
import { BrokerAccountSearchSelector, setCheckedBrokerAccounts, setDatatableSelectedBrokerAccountIndex, setOrderBy, setOrderDirection, setTriggerBrokerAccountSearch } from './reducers/BrokerAccountSearchReducer';
import BrokerAccountApi, { DeleteBrokerAccountByIdParam } from './api/BrokerAccountApi';

const BrokerAccountTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let BrokerAccountData = useSelector(BrokerAccountSearchSelector);
    const BrokerAccountInputViewData = useSelector(BrokerAccountInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const divRef = React.createRef<HTMLDivElement>();

    const columns = [
        {
            Field: "companyName",
            Title: "Contact Name",
            AllowSort: true,
        } as DatatableColumnModel<BrokerAccountModel>,
        {
            Field: "micName",
            Title: "Mic Name",
            AllowSort: true,
        } as DatatableColumnModel<BrokerAccountModel>,
        {
            Field: "accountType",
            Title: "Account Type",
            AllowSort: true,
        } as DatatableColumnModel<BrokerAccountModel>,
        {
            Field: "brokerName",
            Title: "Broker",
            AllowSort: true,
        } as DatatableColumnModel<BrokerAccountModel>,
        {
            Field: "startedDate",
            Title: "Started Date",
            AllowSort: true,
            Render: (rowData: BrokerAccountModel) => (
                <>
                    {Utility.DateFormat.formatDateString(rowData.startedDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<BrokerAccountModel>,
        {
            Field: "expiredDate",
            Title: "Expired Date",
            AllowSort: true,
            Render: (rowData: BrokerAccountModel) => {
                let isExpired = new Date(rowData.expiredDate) < new Date();
                return <span className={isExpired ? 'support-sheet-red-text' : ''}>
                    {Utility.DateFormat.formatDateString(rowData.expiredDate).toUpperCase()}
                </span>
            }
        } as DatatableColumnModel<BrokerAccountModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: BrokerAccountModel) => (
                <>
                    {userProfile.userGroup === "Administrator" &&
                        <Tooltip title="Delete BrokerAccount">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteBrokerAccount(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<BrokerAccountModel>,
    ];

    const deleteBrokerAccount = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this broker account? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                BrokerAccountApi.Delete({
                    id: id
                } as DeleteBrokerAccountByIdParam)
                    .then((response: BaseResponse<BrokerAccountModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("BrokerAccount deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerBrokerAccountSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const getTableClassName = () => {
        let className = "BrokerAccount-datatable-search-result nice-scroll";

        if (!BrokerAccountInputViewData.BrokerAccountInputExpand) {
            className += " BrokerAccount-datatable-search-result-quick-input-collapse-only";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {BrokerAccountData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<BrokerAccountModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={BrokerAccountData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<BrokerAccountModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerBrokerAccountSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(brokerAccount: BrokerAccountModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedBrokerAccountId(brokerAccount.id))
                    dispatch(setDatatableSelectedBrokerAccountIndex(index));
                }
            }}
            selectedRowIndex={BrokerAccountData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={BrokerAccountData.query.orderBy}
            orderDirection={BrokerAccountData.query.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: BrokerAccountModel[]) => {
                dispatch(setCheckedBrokerAccounts(selectedRows));
            }}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
    </div>
};

export default BrokerAccountTable;